/**
 * NOTE: This is a copy of the UserSelector component that assumes the multifilters are not being used.
 */
import * as React from 'react'

import {
  Box,
  CornerIndicator,
  SearchField,
  Skeleton,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import type { FilterOption } from 'modules/core/components/Filters/computeOptions'
import computeOptions from 'modules/core/components/Filters/computeOptions'
import Select from 'modules/core/components/Inputs/Select'
import type { Size } from 'modules/core/components/ScrollableList'
import ScrollableList from 'modules/core/components/ScrollableList'
import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import useFilters from 'modules/core/hooks/useFilters'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import type User from 'modules/core/store/models/User'

import FILTER_OPTIONS from '../Filters/filterOptions'
import { Attachment } from '../TetherComponent/types'
import Controls from './Controls'
import List from './List'
import { GetUserExtraInformationFn } from './List/Item'

const useSearchedUsers = createComputed(
  (filteredUsers: User[], search: string, autoSort: boolean): User[] => {
    const usersToDisplay = filteredUsers.filter((user) => user.matchesFullName(search))

    if (!autoSort) return usersToDisplay

    return sortBy(usersToDisplay, (user) => user.fullName())
  }
)

// NOTE: We are investigating if the multifilters can be removed from the
// component because they are not being used.
const trackUsage = () => {
  track(trackingEvents.employee.employeeSelectorFilters)
}

interface Props {
  loadingOptions: boolean
  users: User[]
  showAll?: boolean
  onCleanFilter?: () => void
  onSelect: (id: number | null | undefined) => void
  onSelectMultiple?: ((ids: number[]) => void) | null
  options?: Record<string, FilterOption[]>
  disabled?: boolean
  mandatory?: boolean
  attachmentPosition?: Attachment
  // TODO: Type better
  selectedIds: number[]
  autoSort?: boolean
  suggestedUsers?: number[]
  confirmedUsers?: number[]
  size?: Size
  setDisplayedUsers?: ((accessIds: number[]) => void) | null | undefined
  setChildPopoverOpen?: (childPopoverOpen: boolean) => void
  disabledUsers?: number[]
  onGetUserExtraInformation?: GetUserExtraInformationFn
  showControls?: boolean
  virtualized?: boolean
  defaultOption?: FilterOption
}

function SimpleUserSelector({
  loadingOptions,
  users,
  showAll = false, // when true, all `users` will be displayed, `multiFilters` and `applyDefaultFilters` ignored
  onCleanFilter,
  onSelect,
  onSelectMultiple,
  selectedIds,
  options,
  disabled = false,
  mandatory = false,
  attachmentPosition = 'bottom center',
  autoSort = true,
  suggestedUsers,
  confirmedUsers,
  size,
  setDisplayedUsers,
  disabledUsers,
  onGetUserExtraInformation,
  showControls = true,
  virtualized = true,
  defaultOption,
}: Props) {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const onSelectUser = (user: User) => () => !disabled && onSelect(user.attributes.id)

  const { loading: loadingFilters, filtersForm, users: filteredUsersFromSelect } = useFilters(users)

  let filteredUsersToUse
  if (showAll) {
    filteredUsersToUse = users
  } else {
    filteredUsersToUse = filteredUsersFromSelect
  }

  const searchedUsers = useSearchedUsers(filteredUsersToUse, debouncedSearch, autoSort).get()

  React.useEffect(() => {
    setDisplayedUsers && setDisplayedUsers(searchedUsers.map((e) => e.attributes.id))
  }, [searchedUsers]) // eslint-disable-line react-hooks/exhaustive-deps

  const [computedOptions, setComputedOptions] = React.useState<Record<string, FilterOption[]>>(
    options ?? {}
  )

  React.useEffect(() => {
    if (!loadingOptions) {
      setComputedOptions(options ?? {})
      if (defaultOption) {
        const defaultLocationValue = Number(defaultOption.value)
        filtersForm.get('filterBy').set(FILTER_OPTIONS.location(defaultLocationValue))
      }
    }
  }, [loadingOptions])

  const { loading: defaultLoadingOptions } = useAsyncData(async () => {
    if (!loadingOptions) {
      setComputedOptions(
        await computeOptions({
          hideFilterByStatus: true,
          showFilterByTeam: true,
        })
      )
    }
  }, [])

  const loading = loadingFilters || loadingOptions || defaultLoadingOptions

  if (loading) {
    return <Skeleton width="full" height="s300" />
  }

  return (
    <Box position="relative">
      {mandatory && (
        <CornerIndicator
          attachmentPosition={attachmentPosition}
          errors={false}
          missing={selectedIds.length == 0}
          missingLabel={i18n.t('common.missing_field')}
        />
      )}
      <BoxList>
        <Box width="full" flexDirection="row" background="grey200" gap="s8" padding="s8">
          <Box width="s140" onClick={() => trackUsage()}>
            <Select options={computedOptions} field={filtersForm.get('filterBy')} />
          </Box>
          <Box flexGrow>
            <SearchField
              variant="big"
              search={search}
              onSearch={onSearch}
              placeholder={i18n.t('common.search')}
            />
          </Box>
        </Box>
        {showControls && (
          <Controls
            users={searchedUsers}
            disabled={disabled}
            selectedIds={selectedIds}
            onSelectMultiple={onSelectMultiple}
            disabledIds={disabledUsers}
          />
        )}
        {virtualized ? (
          <List
            isMultiSelect={!!onSelectMultiple}
            onSelectUser={onSelectUser}
            selectedIds={selectedIds}
            users={searchedUsers}
            suggestedUsers={suggestedUsers}
            confirmedUsers={confirmedUsers}
            disabledUsers={disabledUsers}
            onGetUserExtraInformation={onGetUserExtraInformation}
            size={size}
            virtualized
          />
        ) : (
          <ScrollableList size={size}>
            <List
              isMultiSelect={!!onSelectMultiple}
              onSelectUser={onSelectUser}
              selectedIds={selectedIds}
              users={searchedUsers}
              suggestedUsers={suggestedUsers}
              confirmedUsers={confirmedUsers}
              disabledUsers={disabledUsers}
              onGetUserExtraInformation={onGetUserExtraInformation}
            />
          </ScrollableList>
        )}

        {onCleanFilter && (
          <Box width="full" padding="s12" alignItems="center" justifyContent="center">
            <Touchable onClick={onCleanFilter}>
              <Text size="200" color="radicalRed1200">
                {i18n.t('filters.clean_filter')}
              </Text>
            </Touchable>
          </Box>
        )}
      </BoxList>
    </Box>
  )
}

export default observer(SimpleUserSelector)
